import { IDBPDatabase } from 'idb'
import {
  BaseIndexedDB,
  FileMetadataEntry,
  INDEX_KEYPATH,
  INDEX_NAME,
  STORE_NAME,
  UpgradeTx,
} from './BaseIndexedDB'

/**
 * Safari/WebKit strategy. WebKit does not reliably persist Blobs in IndexedDB (a stored
 * Blob can come back unreadable across sessions, and a Blob re-stored after its source
 * read transaction committed aborts the put — WebKit bug 188438). ArrayBuffers serialize
 * inline and round-trip reliably, so blob bytes are persisted as an ArrayBuffer (+ mime
 * type in `_blobType`) and reconstructed into a fresh in-memory Blob on read.
 *
 * Because bytes are inline, a plain cursor scan deserializes the full payload. To keep
 * metadata-only enumeration cheap, records also carry `_path`/`_kind` and a compound
 * index (`byMeta`) is scanned via key cursor so the bytes are never touched.
 */
export class SafariIndexedDB extends BaseIndexedDB {
  protected get schemaVersion(): number {
    return 2
  }

  protected createSchema(db: IDBPDatabase, tx?: UpgradeTx): void {
    const store = db.objectStoreNames.contains(STORE_NAME)
      ? tx!.objectStore(STORE_NAME)
      : db.createObjectStore(STORE_NAME) // external key, no keyPath
    if (!store.indexNames.contains(INDEX_NAME)) {
      store.createIndex(INDEX_NAME, INDEX_KEYPATH)
    }
  }

  protected async toStorable<T extends object>(value: T, key: string): Promise<T> {
    const v = value as any
    // Index fields: `_path` enables ranged listing by prefix; numeric `_kind` encodes
    // folder-ness (booleans are not valid IDB keys).
    const base = Object.assign({}, value, {
      _path: key,
      _kind: v?.isFolder ? 1 : 0,
    }) as any

    const b = v?.blob
    if (typeof Blob !== 'undefined' && b instanceof Blob) {
      try {
        const buffer = await b.arrayBuffer()
        return Object.assign(base, { blob: buffer, _blobType: b.type }) as T
      } catch {
        // Best-effort: an unreadable source Blob (already-corrupted data) is stored as-is.
        return base as T
      }
    }
    // Already an ArrayBuffer (copied/linked record) or absent — keep as-is.
    return base as T
  }

  protected async hydrateForRead<T>(value: T): Promise<T> {
    const v = value as any
    if (!v) return value
    const b = v.blob
    if (typeof ArrayBuffer !== 'undefined' && b instanceof ArrayBuffer) {
      v.blob = new Blob([b], { type: v._blobType || '' })
    }
    return value
  }

  async listMetadata(prefix: string): Promise<FileMetadataEntry[]> {
    return this._withDBRetry(async db => {
      const tx = this._txRO(db)
      const store = tx.store
      const index = store.index(INDEX_NAME)

      // Bound on [_kind=0 (files only), _path in [prefix, prefix+'\uffff']]. Trailing
      // index components (date/size/fileNumber) are unconstrained, so all files under the
      // prefix are matched and folders (_kind=1) are excluded.
      const range = IDBKeyRange.bound([0, prefix], [0, prefix + '\uffff'])

      const out: FileMetadataEntry[] = []
      const indexedPaths = new Set<string>()
      let cursor = await index.openKeyCursor(range)
      while (cursor) {
        const k = cursor.key as any[]
        const path = String(cursor.primaryKey)
        indexedPaths.add(path)
        out.push({
          path,
          modificationDate: k[2] as Date,
          size: k[3] as number,
          systemFileNumber: k[4] as number,
          isFolder: false,
        })
        cursor = await cursor.continue()
      }

      // Backward compatibility: records created before `_path`/`_kind` existed are valid
      // files but are absent from the metadata index. If we omit them, sync can treat
      // local files as deleted and remove them from cloud. Scan only non-indexed keys.
      // The per-record `store.get` here re-reads blob bytes, but only for un-indexed
      // (legacy) records; each one self-heals into the index on its next save (toStorable
      // stamps `_path`/`_kind`), so this fallback is transitional and shrinks over time.
      const keyRange = IDBKeyRange.bound(prefix, prefix + '\uffff')
      let keyCursor = await store.openKeyCursor(keyRange)
      while (keyCursor) {
        const path = String(keyCursor.primaryKey)
        if (!indexedPaths.has(path)) {
          const record = await store.get(path) as any
          if (record && !record.isFolder) {
            out.push({
              path,
              modificationDate: record.modificationDate as Date,
              size: record.size as number,
              systemFileNumber: record.systemFileNumber as number,
              isFolder: false,
            })
          }
        }
        keyCursor = await keyCursor.continue()
      }

      await tx.done
      return out
    })
  }
}
